import type { ContactFormState } from '../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/ContactManageModal';

export interface ContactValidationErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    mailingStreet?: string;
    mailingCity?: string;
    mailingState?: string;
    mailingZip?: string;
    isPrimaryContact?: string;
}

export type ContactValidationAlert = {
    title: string;
    content?: string;
    fieldGroups?: Array<{ contactName: string; fields: string[] }>;
};

const VALIDATION_FIELD_LABELS: Record<keyof ContactValidationErrors, string> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone Number',
    mailingStreet: 'Street',
    mailingCity: 'City',
    mailingState: 'State',
    mailingZip: 'Zip Code',
    isPrimaryContact: 'Primary Contact',
};

export const PRIMARY_CONTACT_LIMIT_MESSAGE =
    'Only one contact can be designated as the Primary Contact. Please unselect the existing Primary Contact and try again.';

export const DUPLICATE_CONTACT_EMAIL_MESSAGE =
    'Customer contacts cannot share the same email address. Please use a unique email for each contact.';

function normalizeContactEmail(email: string | undefined | null): string {
    return String(email ?? '').trim().toLowerCase();
}

export function buildContactFormStatesForValidation(
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
    savingIndex: number,
    savingData: ContactFormState,
): ContactFormState[] {
    const formStates: ContactFormState[] = customers.map((customer, index) =>
        index === savingIndex ? savingData : resolveContactFormState(customer, overrides[index]),
    );

    if (savingIndex === customers.length) {
        formStates.push(savingData);
    }

    return formStates;
}

/** Indexes of contacts that share an email with at least one other contact. */
export function duplicateContactEmailIndexes(formStates: ContactFormState[]): Set<number> {
    const emailToIndexes = new Map<string, number[]>();

    formStates.forEach((state, index) => {
        const email = normalizeContactEmail(state.email);
        if (!email) return;
        const indexes = emailToIndexes.get(email) ?? [];
        indexes.push(index);
        emailToIndexes.set(email, indexes);
    });

    const duplicates = new Set<number>();
    for (const indexes of emailToIndexes.values()) {
        if (indexes.length > 1) {
            indexes.forEach((index) => duplicates.add(index));
        }
    }

    return duplicates;
}

const TRACKED_FORM_FIELDS: (keyof ContactFormState)[] = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'phoneType',
    'contactType',
    'preferredContactMethod',
    'preferredLanguage',
    'isPrimaryContact',
    'isSecondaryContact',
    'onUtilityBill',
    'addToContract',
    'homeowner',
    'onTitle',
    'sameAsInstallationAddress',
    'mailingStreet',
    'mailingCity',
    'mailingState',
    'mailingZip',
];

export function customerToFormState(c: any): ContactFormState {
    return {
        firstName: c?.FirstName ?? c?.firstName ?? '',
        lastName: c?.LastName ?? c?.lastName ?? '',
        email: c?.Email ?? c?.email ?? '',
        phone: c?.Phone ?? c?.phone ?? '',
        phoneType:
            c?.PhoneType !== undefined && c?.PhoneType !== null
                ? String(c.PhoneType)
                : c?.phoneType !== undefined && c?.phoneType !== null
                  ? String(c.phoneType)
                  : '',
        contactType:
            c?.ContactType !== undefined && c?.ContactType !== null
                ? String(c.ContactType)
                : c?.contactType !== undefined && c?.contactType !== null
                  ? String(c.contactType)
                  : '',
        preferredContactMethod:
            c?.PreferredContactMethod !== undefined && c?.PreferredContactMethod !== null
                ? String(c.PreferredContactMethod)
                : c?.preferredContactMethod !== undefined && c?.preferredContactMethod !== null
                  ? String(c.preferredContactMethod)
                  : '',
        preferredLanguage:
            c?.PreferredLanguage !== undefined && c?.PreferredLanguage !== null
                ? String(c.PreferredLanguage)
                : c?.preferredLanguage !== undefined && c?.preferredLanguage !== null
                  ? String(c.preferredLanguage)
                  : '',
        isPrimaryContact: !!(c?.IsPrimaryContact ?? c?.isPrimaryContact),
        isSecondaryContact: !!(c?.IsSecondaryContact ?? c?.isSecondaryContact),
        onUtilityBill: !!(c?.IsUtilityBillOwner ?? c?.isUtilityBillOwner),
        addToContract: !!(c?.AddToContract ?? c?.addToContract),
        homeowner: !!(c?.OnTitleCheck ?? c?.onTitleCheck)
            ? false
            : !!(c?.IsHomeowner ?? c?.IsHomeOwner ?? c?.isHomeowner ?? c?.isHomeOwner),
        onTitle: !!(c?.OnTitleCheck ?? c?.onTitleCheck),
        sameAsInstallationAddress: c?.SameAsInstallationAddress ?? c?.sameAsInstallationAddress ?? true,
        mailingStreet:
            c?.InsStreet ??
            c?.insStreet ??
            c?.MailingStreet ??
            c?.mailingStreet ??
            c?.OtherStreet ??
            c?.otherStreet ??
            c?.Street ??
            c?.street ??
            '',
        mailingCity:
            c?.InsCity ??
            c?.insCity ??
            c?.MailingCity ??
            c?.mailingCity ??
            c?.OtherCity ??
            c?.otherCity ??
            c?.City ??
            c?.city ??
            '',
        mailingState: (
            c?.InsState ??
            c?.insState ??
            c?.MailingState ??
            c?.mailingState ??
            c?.OtherState ??
            c?.otherState ??
            c?.State ??
            c?.state ??
            ''
        )?.toUpperCase(),
        mailingZip:
            c?.InsZipCode ??
            c?.insZipCode ??
            c?.MailingPostalCode ??
            c?.mailingPostalCode ??
            c?.MailingZipCode ??
            c?.mailingZipCode ??
            c?.OtherZipCode ??
            c?.otherZipCode ??
            c?.ZipCode ??
            c?.zipCode ??
            '',
        companyTag: c?.Company ?? c?.CompanyName ?? c?.company ?? c?.companyName ?? '',
        contactLabel: c?.ContactLabel ?? c?.contactLabel ?? '',
    };
}

export function resolveContactFormState(
    customer: any | null | undefined,
    override?: Partial<ContactFormState>,
): ContactFormState {
    const base = customer ? customerToFormState(customer) : {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        phoneType: '2',
        contactType: '77',
        preferredContactMethod: '6',
        preferredLanguage: '4',
        isPrimaryContact: false,
        isSecondaryContact: false,
        onUtilityBill: false,
        addToContract: false,
        homeowner: false,
        onTitle: false,
        sameAsInstallationAddress: true,
        mailingStreet: '',
        mailingCity: '',
        mailingState: '',
        mailingZip: '',
    };
    return override ? { ...base, ...override } : base;
}

export function isContactFormDirty(saved: ContactFormState, current: ContactFormState): boolean {
    return TRACKED_FORM_FIELDS.some((field) => saved[field] !== current[field]);
}

export function getContactValidationErrors(
    state: ContactFormState,
    options?: { otherPrimaryCount?: number; duplicateEmail?: boolean },
): ContactValidationErrors {
    const newErrors: ContactValidationErrors = {};

    if (!state.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!state.lastName?.trim()) newErrors.lastName = 'Last Name is required';

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
    if (!state.email?.trim()) {
        newErrors.email = 'Valid Email is required';
    } else if (!emailRegex.test(state.email)) {
        newErrors.email = 'Valid Email is required';
    } else if (options?.duplicateEmail) {
        newErrors.email = DUPLICATE_CONTACT_EMAIL_MESSAGE;
    }

    if (!state.phone?.trim()) {
        newErrors.phone = 'Valid Phone Number is required';
    } else {
        const digits = state.phone.replace(/\D/g, '');
        if (digits.length < 10) {
            newErrors.phone = 'Valid Phone Number is required';
        }
    }

    if (!state.sameAsInstallationAddress) {
        if (!state.mailingStreet?.trim()) newErrors.mailingStreet = 'Street is required';
        if (!state.mailingCity?.trim()) newErrors.mailingCity = 'City is required';
        if (!state.mailingState?.trim()) newErrors.mailingState = 'State is required';

        const zipRegex = /^[0-9]{5}$/;
        if (!state.mailingZip?.trim()) {
            newErrors.mailingZip = 'Zip Code is required';
        } else if (!zipRegex.test(state.mailingZip)) {
            newErrors.mailingZip = 'Zip Code is not valid.';
        }
    }

    const otherPrimaryCount = options?.otherPrimaryCount ?? 0;
    if (state.isPrimaryContact && otherPrimaryCount > 0) {
        newErrors.isPrimaryContact = PRIMARY_CONTACT_LIMIT_MESSAGE;
    }

    return newErrors;
}

export function getContactDisplayName(
    index: number,
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
): string {
    if (index >= customers.length) return 'New contact';
    const customer = customers[index];
    const override = overrides[index];
    const firstName = override?.firstName ?? customer?.FirstName ?? customer?.firstName ?? '';
    const lastName = override?.lastName ?? customer?.LastName ?? customer?.lastName ?? '';
    const name = `${firstName} ${lastName}`.trim();
    return name || `Contact ${index + 1}`;
}

export function buildContactValidationAlert(
    errorsByIndex: Record<number, ContactValidationErrors>,
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
): ContactValidationAlert {
    const fieldGroups = Object.entries(errorsByIndex).map(([indexRaw, errors]) => {
        const index = Number(indexRaw);
        const fields = (Object.keys(errors) as (keyof ContactValidationErrors)[]).map(
            (key) => VALIDATION_FIELD_LABELS[key] ?? key,
        );
        return {
            contactName: getContactDisplayName(index, customers, overrides),
            fields,
        };
    });

    return {
        title: fieldGroups.length === 1 ? fieldGroups[0].contactName : 'Fix required fields',
        fieldGroups,
    };
}

export function validateAllContactForms(
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
    savingIndex: number,
    savingData: ContactFormState,
): Record<number, ContactValidationErrors> {
    const formStates = buildContactFormStatesForValidation(
        customers,
        overrides,
        savingIndex,
        savingData,
    );
    const duplicateEmailIndexes = duplicateContactEmailIndexes(formStates);

    const errorsByIndex: Record<number, ContactValidationErrors> = {};

    formStates.forEach((state, index) => {
        const otherPrimaryCount = formStates.filter(
            (row, rowIndex) => rowIndex !== index && row.isPrimaryContact,
        ).length;
        const errors = getContactValidationErrors(state, {
            otherPrimaryCount,
            duplicateEmail: duplicateEmailIndexes.has(index),
        });
        if (Object.keys(errors).length > 0) {
            errorsByIndex[index] = errors;
        }
    });

    return errorsByIndex;
}

export function validateContactBusinessRules(
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
    savingIndex: number,
    savingData: ContactFormState,
): { title: string; content: string } | null {
    const formStates = buildContactFormStatesForValidation(
        customers,
        overrides,
        savingIndex,
        savingData,
    );

    if (duplicateContactEmailIndexes(formStates).size > 0) {
        return {
            title: 'Duplicate Email Address',
            content: DUPLICATE_CONTACT_EMAIL_MESSAGE,
        };
    }

    const primaryCount = formStates.filter((state) => state.isPrimaryContact).length;

    if (primaryCount === 0) {
        return {
            title: 'Primary Contact Required',
            content: 'No contact has been added as a Primary Contact',
        };
    }

    if (primaryCount > 1) {
        return {
            title: 'Primary Contact Limit',
            content: PRIMARY_CONTACT_LIMIT_MESSAGE,
        };
    }

    return null;
}

/** @deprecated Use validateAllContactForms / validateContactBusinessRules — duplicates are blocked, not warned. */
export function getDuplicatePrimarySecondaryEmailWarning(
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
    savingIndex: number,
    savingData: ContactFormState,
): string {
    const formStates = buildContactFormStatesForValidation(
        customers,
        overrides,
        savingIndex,
        savingData,
    );
    return duplicateContactEmailIndexes(formStates).size > 0 ? DUPLICATE_CONTACT_EMAIL_MESSAGE : '';
}

export function hasUnsavedContactChanges(
    customers: any[],
    overrides: Record<number, Partial<ContactFormState>>,
): boolean {
    for (const [indexRaw, override] of Object.entries(overrides)) {
        const index = Number(indexRaw);
        if (!override || Number.isNaN(index)) continue;

        if (index >= customers.length) {
            return true;
        }

        const saved = customerToFormState(customers[index]);
        const current = resolveContactFormState(customers[index], override);
        if (isContactFormDirty(saved, current)) {
            return true;
        }
    }
    return false;
}

function formStateToCustomerFields(state: ContactFormState) {
    return {
        FirstName: state.firstName,
        LastName: state.lastName,
        Email: state.email,
        Phone: state.phone,
        PhoneType: parseInt(state.phoneType, 10) || 2,
        ContactType: parseInt(state.contactType, 10) || 77,
        PreferredContactMethod: parseInt(state.preferredContactMethod, 10) || 6,
        PreferredLanguage: parseInt(state.preferredLanguage, 10) || 4,
        SameAsInstallationAddress: state.sameAsInstallationAddress,
        InsStreet: state.mailingStreet,
        InsCity: state.mailingCity,
        InsState: state.mailingState,
        InsZipCode: state.mailingZip,
        IsUtilityBillOwner: state.onUtilityBill,
        IsHomeOwner: state.homeowner,
        AddToContract: state.addToContract,
        OnTitleCheck: state.onTitle,
    };
}

/** Build the full customer list payload for UpdateContactInformation. */
export function buildUpdatedCustomerListForSave(
    customers: any[],
    contactOverrides: Record<number, Partial<ContactFormState>>,
    savingIndex: number,
    savingData: ContactFormState,
): any[] {
    const resolvedStates = customers.map((customer, index) =>
        index === savingIndex ? savingData : resolveContactFormState(customer, contactOverrides[index]),
    );

    const updatedCustomerList = customers.map((cust, index) => {
        const state = resolvedStates[index];
        const custId = cust?.Id || cust?.CustomerId || 0;
        const roleFields = {
            IsPrimaryContact:
                savingData.isPrimaryContact && index !== savingIndex
                    ? false
                    : state.isPrimaryContact,
            IsSecondaryContact:
                savingData.isSecondaryContact && index !== savingIndex
                    ? false
                    : state.isSecondaryContact,
        };

        return {
            ...cust,
            ...formStateToCustomerFields(state),
            ...roleFields,
            CustomerId: custId,
            index: index + 1,
        };
    });

    if (savingIndex === customers.length) {
        updatedCustomerList.push({
            CustomerId: 0,
            ...formStateToCustomerFields(savingData),
            IsPrimaryContact: savingData.isPrimaryContact,
            IsSecondaryContact: savingData.isSecondaryContact,
            index: customers.length + 1,
        });
    }

    return updatedCustomerList;
}
